import React, { useEffect, useState, useRef } from "react";
import {
    Editor,
    EditorState,
    RichUtils,
    CompositeDecorator,
    KeyBindingUtil,
    getDefaultKeyBinding,
    convertFromRaw,
    convertToRaw,
    ContentBlock,
    ContentState,
    DraftHandleValue,
} from "draft-js";
import "draft-js/dist/Draft.css";
import { useAuth } from "../../../../../auth/auth";
import { saveJobNotesModel } from "../../../../../api/apiCall";
import { saveChangesHistory } from "../../../../../utils/workersUtils";
import { toast } from "react-toastify";
import styles from "./TextEditor.module.css";
import { Button, IconButton, Tooltip } from "@mui/material";
import FormatBoldIcon from "@mui/icons-material/FormatBold";
import FormatItalicIcon from "@mui/icons-material/FormatItalic";
import FormatUnderlinedIcon from "@mui/icons-material/FormatUnderlined";
import StrikethroughSIcon from "@mui/icons-material/StrikethroughS";
import FormatColorFillIcon from "@mui/icons-material/FormatColorFill";
import LooksOneIcon from "@mui/icons-material/LooksOne";
import LooksTwoIcon from "@mui/icons-material/LooksTwo";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import FormatListNumberedIcon from "@mui/icons-material/FormatListNumbered";
import FormatQuoteIcon from "@mui/icons-material/FormatQuote";
import CodeIcon from "@mui/icons-material/Code";
import InsertLinkIcon from "@mui/icons-material/InsertLink";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import UndoIcon from "@mui/icons-material/Undo";
import RedoIcon from "@mui/icons-material/Redo";

// Preserve the exact Draft.js inline style keys used by the previous editor, so
// that existing notes and NotesTable.tsx rendering keep working without change.
const customStyleMap: Record<string, React.CSSProperties> = {
    STRIKETHROUGH: { textDecoration: "line-through" },
    HIGHLIGHT: { backgroundColor: "yellow" },
};

const SAVE_COMMAND = "editor-save";

const findLinkEntities = (
    contentBlock: ContentBlock,
    callback: (start: number, end: number) => void,
    contentState: ContentState
) => {
    contentBlock.findEntityRanges((character) => {
        const entityKey = character.getEntity();
        return (
            entityKey !== null &&
            contentState.getEntity(entityKey).getType() === "LINK"
        );
    }, callback);
};

const LinkSpan = (props: any) => {
    const { url } = props.contentState.getEntity(props.entityKey).getData();
    return (
        <a href={url} style={{ color: "#3b5998", textDecoration: "underline" }}>
            {props.children}
        </a>
    );
};

const linkDecorator = new CompositeDecorator([
    { strategy: findLinkEntities, component: LinkSpan },
]);

// Same load path the previous editor used: convertFromRaw(JSON.parse(defaultValue)).
const buildEditorState = (raw: any): EditorState => {
    if (!raw || raw === "null") {
        return EditorState.createEmpty(linkDecorator);
    }
    try {
        const parsed = typeof raw === "string" ? JSON.parse(raw) : raw;
        return EditorState.createWithContent(convertFromRaw(parsed), linkDecorator);
    } catch (err) {
        return EditorState.createEmpty(linkDecorator);
    }
};

// Plain-text version of a stored notesJson, used for version history entries.
const getPlainText = (raw: any): string | null => {
    if (!raw || raw === "null") return null;
    try {
        const parsed = typeof raw === "string" ? JSON.parse(raw) : raw;
        const text = parsed.blocks.map((block: any) => block.text.trim()).join(" ").trim();
        return text || null;
    } catch (err) {
        return null;
    }
};

const TextEditor = ({ id, data, getEditJobModal, connection, week, year }: any) => {
    const { state } = useAuth();
    const [notesJson, setNotesJson] = useState(data.notesJson === "null" ? null : data.notesJson)
    const [editorState, setEditorState] = useState<EditorState>(() =>
        buildEditorState(data.notesJson === "null" ? null : data.notesJson)
    );
    const editorRef = useRef<any>(null);
    const savedNotesRef = useRef<string | null>(getPlainText(notesJson));

    const handleSave = async () => {
        const rawData = JSON.stringify(convertToRaw(editorState.getCurrentContent()));

        let parsedData;
        try {
            parsedData = JSON.parse(rawData);
        } catch (err) {
            toast.error("Failed to save. Try again.");
            return;
        }

        const notes = parsedData.blocks.map((block: any) => block.text.trim()).join(" ");
        const hasContent = parsedData.blocks.some((block: any) => block.text.trim() !== "");

        const notesJson = hasContent ? JSON.stringify(parsedData) : null;

        if (notesJson) {
            try {
                JSON.parse(notesJson);
            } catch (err) {
                toast.error("Failed to save. Try again.");
                return;
            }
        }

        const response = await saveJobNotesModel(state.user?.token, id, notes || null, notesJson);
        if (response) {
            connection.invoke("EditJobNotesJson", `${week}-${year}`, id, notesJson);
            toast.success("Added Notes successfully");

            const oldNotes = savedNotesRef.current;
            const newNotes = notes.trim() || null;
            if (oldNotes !== newNotes) {
                savedNotesRef.current = newNotes;
                await saveChangesHistory(state.user?.token, id, [
                    { property: "notes", oldValue: oldNotes, newValue: newNotes },
                ]);
            }
        } else {
            toast.error("Something went wrong!");
        }

        getEditJobModal();
    };

    useEffect(() => {
        if(connection) {
            connection.on('EditJobNotesJson', (jobID: any, notesJson: any)=>{
                if(data.id === jobID) {
                    setNotesJson(notesJson)
                }
            })

            return () => {
                connection.off('EditJobNotesJson')
            }
        }
    }, [connection])

    // Re-seed the editor when notesJson changes (e.g. a remote SignalR update),
    // mirroring the previous editor re-initialising on defaultValue change.
    useEffect(() => {
        setEditorState(buildEditorState(notesJson));
        savedNotesRef.current = getPlainText(notesJson);
    }, [notesJson]);

    const focusEditor = () => editorRef.current?.focus();

    const handleKeyCommand = (command: string, stateArg: EditorState): DraftHandleValue => {
        if (command === SAVE_COMMAND) {
            handleSave();
            return "handled";
        }
        const newState = RichUtils.handleKeyCommand(stateArg, command);
        if (newState) {
            setEditorState(newState);
            return "handled";
        }
        return "not-handled";
    };

    const keyBindingFn = (e: React.KeyboardEvent): string | null => {
        if (KeyBindingUtil.hasCommandModifier(e) && e.key === "s") {
            e.preventDefault();
            return SAVE_COMMAND;
        }
        return getDefaultKeyBinding(e);
    };

    const toggleInline = (style: string) => (e: React.MouseEvent) => {
        e.preventDefault();
        setEditorState(RichUtils.toggleInlineStyle(editorState, style));
    };

    const toggleBlock = (blockType: string) => (e: React.MouseEvent) => {
        e.preventDefault();
        setEditorState(RichUtils.toggleBlockType(editorState, blockType));
    };

    const undo = (e: React.MouseEvent) => {
        e.preventDefault();
        setEditorState(EditorState.undo(editorState));
    };

    const redo = (e: React.MouseEvent) => {
        e.preventDefault();
        setEditorState(EditorState.redo(editorState));
    };

    const addLink = (e: React.MouseEvent) => {
        e.preventDefault();
        const selection = editorState.getSelection();
        if (selection.isCollapsed()) {
            toast.info("Select the text you want to turn into a link.");
            return;
        }
        const url = window.prompt("Enter URL:") || "";
        if (!url) return;
        const contentWithEntity = editorState
            .getCurrentContent()
            .createEntity("LINK", "MUTABLE", { url });
        const entityKey = contentWithEntity.getLastCreatedEntityKey();
        const withContent = EditorState.set(editorState, { currentContent: contentWithEntity });
        setEditorState(
            RichUtils.toggleLink(withContent, withContent.getSelection(), entityKey)
        );
    };

    const removeLink = (e: React.MouseEvent) => {
        e.preventDefault();
        const selection = editorState.getSelection();
        if (!selection.isCollapsed()) {
            setEditorState(RichUtils.toggleLink(editorState, selection, null));
        }
    };

    const currentInline = editorState.getCurrentInlineStyle();
    const currentBlockType = RichUtils.getCurrentBlockType(editorState);

    const inlineBtn = (style: string, title: string, Icon: any) => (
        <Tooltip title={title}>
            <IconButton
                size="small"
                onMouseDown={toggleInline(style)}
                color={currentInline.has(style) ? "primary" : "default"}
            >
                <Icon fontSize="small" />
            </IconButton>
        </Tooltip>
    );

    const blockBtn = (blockType: string, title: string, Icon: any) => (
        <Tooltip title={title}>
            <IconButton
                size="small"
                onMouseDown={toggleBlock(blockType)}
                color={currentBlockType === blockType ? "primary" : "default"}
            >
                <Icon fontSize="small" />
            </IconButton>
        </Tooltip>
    );

    return (
        <>
            <div className={styles.centerTextEditor}>
                <div className={styles.border}>
                    <div className={styles.editorWrapper}>
                        <div className={styles.toolbar}>
                            {inlineBtn("BOLD", "Bold", FormatBoldIcon)}
                            {inlineBtn("ITALIC", "Italic", FormatItalicIcon)}
                            {inlineBtn("UNDERLINE", "Underline", FormatUnderlinedIcon)}
                            {inlineBtn("STRIKETHROUGH", "Strikethrough", StrikethroughSIcon)}
                            {inlineBtn("HIGHLIGHT", "Highlight", FormatColorFillIcon)}
                            {blockBtn("header-one", "Heading 1", LooksOneIcon)}
                            {blockBtn("header-two", "Heading 2", LooksTwoIcon)}
                            {blockBtn("unordered-list-item", "Bulleted list", FormatListBulletedIcon)}
                            {blockBtn("ordered-list-item", "Numbered list", FormatListNumberedIcon)}
                            {blockBtn("blockquote", "Quote", FormatQuoteIcon)}
                            {blockBtn("code-block", "Code block", CodeIcon)}
                            <Tooltip title="Add link">
                                <IconButton size="small" onMouseDown={addLink}>
                                    <InsertLinkIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="Remove link">
                                <IconButton size="small" onMouseDown={removeLink}>
                                    <LinkOffIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="Undo">
                                <IconButton size="small" onMouseDown={undo}>
                                    <UndoIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="Redo">
                                <IconButton size="small" onMouseDown={redo}>
                                    <RedoIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        </div>
                        <div className={styles.editor} onClick={focusEditor}>
                            <Editor
                                ref={editorRef}
                                editorState={editorState}
                                onChange={setEditorState}
                                handleKeyCommand={handleKeyCommand}
                                keyBindingFn={keyBindingFn}
                                customStyleMap={customStyleMap}
                                placeholder="Start typing..."
                                spellCheck
                            />
                        </div>
                    </div>
                </div>
            </div>
            <Button
                variant="contained"
                style={{boxShadow: "none", margin: '0 auto', marginTop: 20, display: 'flex'}}
                onClick={() => handleSave()}
            >
                Save Notes
            </Button>
        </>
    );
};

export default TextEditor;
